/**
 * Button Component
 *
 * 신규 디자인 (color 지정 시 적용)
 *   <Button color="primary">저장</Button>                         // fill, md
 *   <Button color="error" appearance="outline" size="sm">삭제</Button>
 *   - color: primary | secondary | success | error | warning | info | gray
 *   - appearance: fill(기본) | outline
 *   - size: sm | md(기본) | lg
 *
 * 기존 디자인 (color 미지정 시) - variant: primary | secondary | outline | ghost
 *   기존 페이지 호환용. 새 화면은 color 방식 사용
 */

import { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonColor = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'gray';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  color?: ButtonColor;
  appearance?: 'fill' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  /** 기존 디자인용 (color 미지정 시만 적용) - 새 화면은 color 사용 */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  children: ReactNode;
}

const legacyClass = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  outline: 'btn-outline',
  ghost: 'btn-ghost',
};

export function Button({
  color,
  appearance = 'fill',
  size='md',
  variant = 'primary',
  children,
  className = '',
  type = 'button',
  ...props
}: ButtonProps) {
  // color 또는 size 중 하나라도 지정되면 신규 디자인(.btn) 체계를 사용한다.
  // 둘 다 없으면 기존 legacy(variant) 디자인을 그대로 유지한다.
  const useNewDesign = color !== undefined || size !== undefined;
  const classes = useNewDesign
    ? `btn btn--${color ?? 'primary'} btn--${appearance} btn--${size ?? 'md'}`
    : legacyClass[variant];

  return (
    <button type={type} className={`${classes} ${className}`.trim()} {...props}>
      {children}
    </button>
  );
}

export default Button;
